import {
  fetchProductCareCopy as fetchContenfulProductCareCopy,
  fetchProductDiscountDescription
} from '@api/graphqlContentFul'
import { getValueFromObject } from '@helpers/utils'
import {
  BenefitsProps,
  ContentfulDocument,
  ProductDiscountItem,
  StatementSectionProps
} from 'types/contentful/graphQlApi'
import {
  fetchContentfulProductsBreakdownById,
  fetchContentfulProductsBreakdownByProductCategory
} from '@api/graphqlContentFul/productsBreakdown'
import { CONTENTFUL_HEADERS_ID } from '@consts/index'
import { searchDataByAttribute } from '@helpers/contentful'
import { formatGroupType, yotpoRatingsHandler } from './yotpo/ratings'
import { DiscountByProductType } from '@lib/productPageUtils'
import logBuildError from '@helpers/misc/logBuildError'

const handleProductsBuildError = logBuildError('products')

async function getProductsBreakdown(
  productType: string,
  productCategory: string,
  preview = false
) {
  if (productType === 'Gift Card') {
    const { productsBreakdown } = (await fetchContentfulProductsBreakdownById({
      id: CONTENTFUL_HEADERS_ID.productBreakdownGiftingCollection,
      preview
    })) || { productsBreakdown: { items: [] } }
    return productsBreakdown
  }

  const { productsBreakdown } =
    (await fetchContentfulProductsBreakdownByProductCategory({
      productCategory,
      preview
    })) || { productsBreakdown: { items: [] } }

  return productsBreakdown?.items[0]
}

async function fetchProductCareCopy(productType: string, preview = false) {
  const productCareCopy = await fetchContenfulProductCareCopy({
    productType,
    preview
  })
  return getValueFromObject<ContentfulDocument | null>(
    productCareCopy,
    'productCareCopy.items[0].copy.json',
    null
  )
}

async function fetchProductDiscount(productTypes: string[] = []) {
  const discountByProductType: DiscountByProductType = {}
  // eslint-disable-next-line no-restricted-syntax
  for (const productType of productTypes) {
    // eslint-disable-next-line no-await-in-loop
    const productDiscountResponse = await fetchProductDiscountDescription({
      productType
    })
    const discount = getValueFromObject<ProductDiscountItem | null>(
      productDiscountResponse,
      'productDiscount.items[0]',
      null
    )
    discountByProductType[productType] = discount
      ? {
          title: discount.discountTitle,
          description: discount.discountDescription
        }
      : {}
  }
  return discountByProductType
}

async function fetchProductContent({
  productTypes,
  handle,
  isPreview = false,
  contentType
}: {
  productTypes: string[]
  handle: string
  isPreview?: boolean
  contentType: string
}) {
  let productTypesContent = null

  for (let i = 0; i < productTypes.length; i++) {
    const productType = productTypes[i]

    try {
      const response = await searchDataByAttribute<
        BenefitsProps | StatementSectionProps
      >(
        {
          content_type: contentType,
          'fields.productTypes': productType
        },
        isPreview
      )

      productTypesContent =
        Object.keys(response).length > 0
          ? (response as BenefitsProps | StatementSectionProps)
          : null

      if (i === 0 && response) {
        break
      }
    } catch (error) {
      handleProductsBuildError({
        errorLevel: 'error',
        properties: {
          handle,
          message: `Failed to fetch benefits for ${handle}`,
          error
        }
      })
      productTypesContent = null
    }
  }

  return productTypesContent
}

const ratingsHandler = yotpoRatingsHandler()

const fetchProductRatings = async (yotpoProductType: string) => {
  const grouped = await ratingsHandler.getByGroup()
  return grouped?.[yotpoProductType] ?? null
}

type CMSInput = {
  productType: string
  productCategory: string
  productTypes: string[]
  productHandle: string
  preview?: boolean
}

export async function fetchProductPageCMSData({
  productType,
  productCategory,
  productTypes,
  productHandle,
  preview = false
}: CMSInput) {
  const yotpoProductType = formatGroupType(productType)
  const [
    productsBreakdown,
    careCopy,
    productBenefits,
    productStatementSection,
    discountByProductType,
    yotpoProductRatings
  ] = await Promise.all([
    getProductsBreakdown(productType, productCategory, preview),
    fetchProductCareCopy(productType, preview),
    fetchProductContent({
      productTypes: productTypes,
      handle: productHandle,
      contentType: 'benefits',
      isPreview: preview
    }),
    fetchProductContent({
      productTypes: productTypes,
      handle: productHandle,
      contentType: 'statementSection',
      isPreview: preview
    }),
    fetchProductDiscount(productTypes),
    fetchProductRatings(yotpoProductType)
  ])

  return {
    productsBreakdown: productsBreakdown || null,
    careCopy: careCopy || null,
    productBenefits,
    productStatementSection,
    discountByProductType,
    starRatings: yotpoProductRatings
  }
}
